import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost } from './client';

export interface AdminCaseSummary {
  id: string;
  org_id: string;
  org_name: string;
  org_region: string;
  patient_id: string;
  patient_given_name: string;
  patient_family_name: string;
  product_id: string;
  product_code: string;
  product_display_name: string;
  status: string;
  opened_at: string;
  updated_at: string;
  dermoscopic_image_count: number;
  macroscopic_image_count: number;
}

export interface AdminFindingImage {
  id: string;
  ingestion_status: string;
  ingestion_retry_count: number;
  mime_type: string;
  capture_type: string;
  width_px: number | null;
  height_px: number | null;
  created_at: string;
  updated_at: string;
  is_primary: boolean;
}

export interface AdminFinding {
  id: string;
  finding_type: string;
  body_site: string | null;
  body_site_free_text: string | null;
  clinical_notes: string | null;
  parent_finding_id: string | null;
  created_at: string;
  images: AdminFindingImage[];
}

export interface AdminCaseDetail extends AdminCaseSummary {
  patient_dob: string;
  external_reference: string | null;
  clinical_context: Record<string, unknown> | null;
  custom_fields: Record<string, string> | null;
  created_at: string;
  findings: AdminFinding[];
  relations: {
    findings_count: number;
    images: {
      pending: number;
      processed: number;
      quarantined: number;
      failed: number;
      total: number;
    };
    diagnoses_count: number;
    histology_reports_count: number;
  };
}

export interface TimelineEvent {
  id: string;
  timestamp: string;
  event_type: string;
  resource_type: string;
  resource_id: string;
  summary: string;
  payload: Record<string, unknown>;
  correlation_id: string | null;
  actor: {
    display_name?: string;
    product_code?: string;
  } | null;
}

export interface AdminCaseList {
  items: AdminCaseSummary[];
  total: number;
  page: number;
  limit: number;
}

export interface TimelineList {
  items: TimelineEvent[];
  next_cursor: string | null;
}

export interface ListCasesFilters {
  orgId?: string;
  status?: 'open' | 'awaiting_histology' | 'completed';
  patientId?: string;
  search?: string;
  openedAfter?: string;
  openedBefore?: string;
  page?: number;
  limit?: number;
}

export interface ClientApiRequest {
  id: string;
  section: 'case' | 'session';
  service: 'clinical-api' | 'auth';
  method: string;
  path: string;
  status: number;
  duration_ms: number;
  timestamp: string;
  correlation_id: string | null;
  client_id: string;
  headers: Record<string, string>;
  body: unknown | null;
  response_body: unknown | null;
}

export interface CaseRequestsResponse {
  case_requests: ClientApiRequest[];
  client_session: ClientApiRequest[];
  truncated: boolean;
}

export interface CaseRequestFilters {
  method?: string;
  status?: number;
  q?: string;
}

const KEYS = {
  list: (filters: ListCasesFilters) => ['cases', 'list', filters] as const,
  detail: (id: string) => ['cases', 'detail', id] as const,
  timeline: (id: string, cursor?: string) => ['cases', 'timeline', id, cursor ?? null] as const,
  requests: (id: string, filters: CaseRequestFilters) =>
    ['cases', 'requests', id, filters] as const,
  aiReviews: (id: string) => ['cases', 'ai-reviews', id] as const,
};

function buildListUrl(filters: ListCasesFilters): string {
  const params = new URLSearchParams();
  if (filters.orgId) params.set('org_id', filters.orgId);
  if (filters.status) params.set('status', filters.status);
  if (filters.patientId) params.set('patient_id', filters.patientId);
  if (filters.search) params.set('search', filters.search);
  if (filters.openedAfter) params.set('opened_after', filters.openedAfter);
  if (filters.openedBefore) params.set('opened_before', filters.openedBefore);
  if (filters.page !== undefined) params.set('page', String(filters.page));
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  const qs = params.toString();
  return qs ? `/api/cases?${qs}` : '/api/cases';
}

// Cases list polls on a 30-second cadence so operators see new simulator
// cases without refreshing. Pauses when the tab is hidden to avoid
// burning API quota on inactive windows; React Query refetches on focus.
export const CASES_REFRESH_MS = 30_000;

export function useCases(filters: ListCasesFilters = {}) {
  return useQuery({
    queryKey: KEYS.list(filters),
    queryFn: () => apiGet<AdminCaseList>(buildListUrl(filters)),
    refetchInterval: CASES_REFRESH_MS,
    refetchIntervalInBackground: false,
  });
}

export function useCase(id: string | undefined) {
  return useQuery({
    queryKey: id ? KEYS.detail(id) : ['cases', 'detail', 'idle'],
    queryFn: () => apiGet<AdminCaseDetail>(`/api/cases/${id}`),
    enabled: !!id,
  });
}

export function useCaseTimeline(id: string | undefined, cursor?: string) {
  return useQuery({
    queryKey: id ? KEYS.timeline(id, cursor) : ['cases', 'timeline', 'idle'],
    queryFn: () => {
      const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
      return apiGet<TimelineList>(`/api/cases/${id}/timeline${qs}`);
    },
    enabled: !!id,
  });
}

export interface UpdateCaseInput {
  clinicalContext?: Record<string, unknown>;
  customFields?: Record<string, string>;
  externalReference?: string;
  reason: string;
}

export function useUpdateCase(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateCaseInput) =>
      apiPatch<AdminCaseDetail>(`/api/cases/${caseId}`, {
        clinical_context: input.clinicalContext,
        custom_fields: input.customFields,
        external_reference: input.externalReference,
        reason: input.reason,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.detail(caseId) }),
  });
}

export interface UpdateCaseStatusInput {
  status: 'open' | 'awaiting_histology' | 'completed';
  reason: string;
}

export interface UpdateCaseStatusResponse {
  id: string;
  status: string;
}

export function useUpdateCaseStatus(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateCaseStatusInput) =>
      apiPatch<UpdateCaseStatusResponse>(`/api/cases/${caseId}/status`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.detail(caseId) }),
  });
}

export interface AdminCaseImageUploadedBy {
  display_name?: string;
  source?: string;
  product_name?: string;
}

export interface AdminCaseImage {
  id: string;
  finding_id: string;
  mime_type: string;
  capture_type: string;
  width_px: number | null;
  height_px: number | null;
  is_primary: boolean;
  created_at: string;
  presigned_url: string;
  expires_at: string;
  uploaded_by?: AdminCaseImageUploadedBy | null;
}

export interface AdminCaseImagesList {
  items: AdminCaseImage[];
}

export function useCaseImages(caseId: string | undefined) {
  return useQuery({
    queryKey: caseId ? ['cases', 'images', caseId] : ['cases', 'images', 'idle'],
    queryFn: () => apiGet<AdminCaseImagesList>(`/api/cases/${caseId}/images`),
    enabled: !!caseId,
    // Presigned URLs expire after 15 minutes; refetch every 12 to keep
    // ahead of expiry without burning network.
    staleTime: 12 * 60_000,
    gcTime: 15 * 60_000,
  });
}

export interface RemoveCaseImageInput {
  findingId: string;
  imageId: string;
  reason: string;
}

export function useRemoveCaseImage(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RemoveCaseImageInput) =>
      apiDelete<void>(`/api/cases/${caseId}/findings/${input.findingId}/images/${input.imageId}`, {
        reason: input.reason,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cases', 'images', caseId] }),
  });
}

export interface RetryCaseImageInput {
  findingId: string;
  imageId: string;
  reason: string;
}

export function useRetryCaseImage(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RetryCaseImageInput) =>
      apiPost<AdminFindingImage>(
        `/api/cases/${caseId}/findings/${input.findingId}/images/${input.imageId}/retry`,
        { reason: input.reason },
      ),
    // The retry count and ingestion_status live on the case-detail findings
    // payload, so refresh that query to reflect the incremented count / new
    // status inline.
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.detail(caseId) }),
  });
}

export interface AddCaseImageInput {
  findingId: string;
  mimeType: string;
  captureType: 'dermoscopic' | 'macroscopic' | 'other';
  isPrimary?: boolean;
  reason: string;
}

export interface AddCaseImageResponse {
  image_id: string;
  upload_url: string;
}

export function useAddCaseImage(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddCaseImageInput) =>
      apiPost<AddCaseImageResponse>(`/api/cases/${caseId}/findings/${input.findingId}/images`, {
        mime_type: input.mimeType,
        capture_type: input.captureType,
        is_primary: input.isPrimary,
        reason: input.reason,
      }),
    // Refresh both the thumbnails query and the case-detail query — the
    // finding's images (and their ingestion_status, which gates the per-lesion
    // "Run AI review" button) live on the case-detail findings payload (#975).
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cases', 'images', caseId] });
      void qc.invalidateQueries({ queryKey: KEYS.detail(caseId) });
    },
  });
}

export interface ConfirmCaseImageInput {
  findingId: string;
  imageId: string;
}

export interface ConfirmCaseImageResponse {
  id: string;
  ingestion_status: string;
}

export function useConfirmCaseImage(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ConfirmCaseImageInput) =>
      apiPost<ConfirmCaseImageResponse>(
        `/api/cases/${caseId}/findings/${input.findingId}/images/${input.imageId}/process`,
      ),
    // A confirmed image becomes `processed`, which the per-lesion "Run AI
    // review" button reads from the case-detail findings payload — refresh that
    // query (not just the thumbnails) so the button enables without a manual
    // reload (#975).
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cases', 'images', caseId] });
      void qc.invalidateQueries({ queryKey: KEYS.detail(caseId) });
    },
  });
}

export interface AiReviewTriggerResponse {
  correlationId: string;
}

export interface TriggerAiReviewInput {
  reason: string;
  dermoscopyExceptionReason?: string;
  dermoscopyExceptionNote?: string;
}

export function useTriggerAiReview(caseId: string) {
  return useMutation({
    mutationFn: (input: TriggerAiReviewInput) => {
      const body: Record<string, unknown> = { reason: input.reason };
      if (input.dermoscopyExceptionReason)
        body.dermoscopy_exception_reason = input.dermoscopyExceptionReason;
      if (input.dermoscopyExceptionNote)
        body.dermoscopy_exception_note = input.dermoscopyExceptionNote;
      return apiPost<AiReviewTriggerResponse>(`/api/cases/${caseId}/ai-reviews`, body);
    },
  });
}

export interface TriggerAiReviewForFindingInput {
  findingId: string;
  reason: string;
  dermoscopyExceptionReason?: string;
  dermoscopyExceptionNote?: string;
}

export function useTriggerAiReviewForFinding(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TriggerAiReviewForFindingInput) => {
      const body: Record<string, unknown> = { reason: input.reason };
      if (input.dermoscopyExceptionReason)
        body.dermoscopy_exception_reason = input.dermoscopyExceptionReason;
      if (input.dermoscopyExceptionNote)
        body.dermoscopy_exception_note = input.dermoscopyExceptionNote;
      return apiPost<AiReviewTriggerResponse>(
        `/api/cases/${caseId}/findings/${input.findingId}/ai-reviews`,
        body,
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.aiReviews(caseId) }),
  });
}

export interface AiReviewLesion {
  id: string;
  lesionNumber: number;
  classification: string;
  category: string;
  priority: number;
  suspectedDiagnosisSnomedCode: string | null;
  suspectedDiagnosisSnomedTerm: string | null;
  suspectedDiagnosisIcd10Code: string | null;
  suspectedDiagnosisIcd10Term: string | null;
  referralFlag: string | null;
  referralRecommendation: string | null;
  interpretationGuidance: string | null;
  coordinates: unknown;
}

export interface AiReviewResult {
  analysisResult: string;
  analysisStatus: string;
  lesionCount: number;
  processingType: string;
  sensitivitySet: string | null;
  reportDate: string;
}

export interface AiReviewImageRef {
  clinicalImageId: string;
  imageType: string;
}

export interface AiReviewSummary {
  id: string;
  status: string;
  findingId: string | null;
  errorClassification: string | null;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
  lesions: AiReviewLesion[];
  result: AiReviewResult | null;
  images?: AiReviewImageRef[];
}

export interface AiReviewsListResponse {
  data: AiReviewSummary[];
}

const AI_REVIEW_TERMINAL = new Set(['completed', 'failed', 'cancelled', 'superseded']);

export function aiReviewRefetchInterval(
  data: AiReviewsListResponse | undefined,
  keepPolling: boolean,
): number | false {
  if (keepPolling) return 5_000;
  if (!data) return 5_000;
  return data.data.some((r) => !AI_REVIEW_TERMINAL.has(r.status)) ? 5_000 : false;
}

export function useAiReviews(caseId: string, keepPolling = false) {
  return useQuery({
    queryKey: KEYS.aiReviews(caseId),
    queryFn: () => apiGet<AiReviewsListResponse>(`/api/cases/${caseId}/ai-reviews`),
    refetchInterval: (query) => aiReviewRefetchInterval(query.state.data, keepPolling),
  });
}

export function useCaseRequests(id: string | undefined, filters: CaseRequestFilters = {}) {
  return useQuery({
    queryKey: id ? KEYS.requests(id, filters) : ['cases', 'requests', 'idle'],
    queryFn: () => {
      const p = new URLSearchParams();
      if (filters.method) p.set('method', filters.method);
      if (filters.status) p.set('status', String(filters.status));
      if (filters.q) p.set('q', filters.q);
      const qs = p.toString() ? `?${p.toString()}` : '';
      return apiGet<CaseRequestsResponse>(`/api/cases/${id}/requests${qs}`);
    },
    enabled: !!id,
  });
}
